import { Injectable } from '@angular/core';
import {
  OrganisationClassificationModel,
  OrganisationModel,
  OrganisationSearchResultModel,
  OrganisationStaffTypeModel,
} from '@fleet/model';
import { OrganisationClassificationApiService } from '@fleet/api/organisation/organisation-classification-api.service';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';

import { OrganisationService } from '../organisation.service';
import {
  DEFAULT_ORGANISATION_LABELS,
  OrganisationLabels,
} from './organisation-labels.model';

const EMPTY_CLASSIFICATION: OrganisationClassificationModel = {
  segments: [],
  staffTypes: [],
};

/**
 * The one place the Hub turns a staff type code into the words a screen shows. The organisation staff entity,
 * its field names (`employeeNumber`, `organisationStaffId`) and its routes never change — only the label does,
 * and it comes from `GET /organisation/classification`, not a table here.
 *
 * The classification is reference data seeded by Liquibase, so it is fetched once per session (`shareReplay`)
 * rather than once per form.
 */
@Injectable({
  providedIn: 'root',
})
export class OrganisationLabelsService {
  /**
   * The active segments (each with its active categories) and staff types, shared so a form that needs the
   * classification for its own purpose (e.g. the organisation search's Segment/Category selects) reads the
   * same cached call this service already makes, rather than fetching it again.
   */
  readonly classification$: Observable<OrganisationClassificationModel>;

  constructor(
    private classificationApiService: OrganisationClassificationApiService,
    private organisationService: OrganisationService
  ) {
    this.classification$ = this.classificationApiService.get().pipe(
      map((resp: any) => resp?.data ?? EMPTY_CLASSIFICATION),
      catchError(() => of(EMPTY_CLASSIFICATION)),
      shareReplay({ bufferSize: 1, refCount: false })
    );
  }

  /** The labels for the current organisation (`OrganisationService.organisation$`), re-emitting once the classification arrives. */
  get labels$(): Observable<OrganisationLabels> {
    return this.organisationService.organisation$.pipe(
      switchMap((organisation) => this.labelsFor(organisation))
    );
  }

  /** For a component that has the organisation (or a search result row) but not necessarily the current-organisation stream. */
  labelsFor(
    organisation:
      | OrganisationModel
      | OrganisationSearchResultModel
      | null
      | undefined
  ): Observable<OrganisationLabels> {
    if (!organisation?.category) {
      return of({ ...DEFAULT_ORGANISATION_LABELS });
    }
    return this.classification$.pipe(
      map((classification) =>
        this.defaultStaffTypeForCategory(organisation.category, classification)
      ),
      switchMap((code) => this.labelsForStaffType(code))
    );
  }

  /** For the few places that have a staff record's `type` but not the organisation (e.g. staff onboarding/detail). */
  labelsForStaffType(code: string | null | undefined): Observable<OrganisationLabels> {
    if (!code) {
      return of({ ...DEFAULT_ORGANISATION_LABELS });
    }
    return this.classification$.pipe(
      map((classification) =>
        this.toLabels(classification.staffTypes?.find((type) => type.code === code))
      )
    );
  }

  private defaultStaffTypeForCategory(
    categoryCode: string,
    classification: OrganisationClassificationModel
  ): string | undefined {
    for (const segment of classification.segments ?? []) {
      const category = segment.categories?.find((c) => c.code === categoryCode);
      if (category) {
        return category.defaultStaffType;
      }
    }
    return undefined;
  }

  private toLabels(staffType: OrganisationStaffTypeModel | undefined): OrganisationLabels {
    if (!staffType) {
      return { ...DEFAULT_ORGANISATION_LABELS };
    }
    // The platform's own words for a business's staff stay "Staff member" / "Staff", not the API's "Employee".
    if (staffType.code === 'EMPLOYEE') {
      return {
        singular: DEFAULT_ORGANISATION_LABELS.singular,
        plural: DEFAULT_ORGANISATION_LABELS.plural,
        reference: staffType.referenceLabel || DEFAULT_ORGANISATION_LABELS.reference,
        type: 'EMPLOYEE',
      };
    }
    return {
      singular: staffType.name,
      plural: `${staffType.name}s`,
      reference: staffType.referenceLabel,
      type: staffType.code,
    };
  }
}
